import React, { useEffect, useState } from "react";

// custom components
import JobList from "../job-components/job-list";
import EmployeeInformation from "../employee-components/employee-information";
import TimelineColumnContainer from "./timeline-column-container";
import TimelineThirtyMinColumnContainer from "./timeline-thirtyMin-column-container";


// styling
import { Colors } from "_styles";
import styled from 'styled-components';

// time formatting
const timeFormatter = require("_utils/date-time-functions");

// timeline constants
import { EmployeeInformationColumnWidth, minuteWidth, minute30MinLayoutWidth } from "../../../../utils/timeline-constants";

// redux
import { useSelector, useDispatch } from "react-redux";
import { setShowConfirmationModal, selectSelectedTasksList, setShowBreakTaskModal, setBreakTaskEmployee } from "_redux/slices/timeline/selected-task";
import { selectThirtyMinLayout, selectTimelineDate } from "_redux/slices/timeline/timeline-control";
import { selectTimelineData } from "_redux/slices/timeline/timeline-data";

// functions 
import { convertShiftTimeStamp, convertTimeStampToDate, formatTimeToNearest30, getFiveMinuteIntervalIndex } from "_utils/functions";
import ThirtyMinLayoutJobList from "../job-components/thirtyMin-layout-job-list";
import { setClickedEmployeeId } from "_redux/slices/timeline/employees";

const IntervalLine = styled.div`
position: absolute;
top: 0;
bottom: 0;
width: 1px;
background-color: transparent;
`;

const TimelineThirtyMinRow = (props) => {

    //#region redux

    const dispatch = useDispatch();
    const timelineData = useSelector(selectTimelineData);                                 // timeline data object
    const selectedTasksList = useSelector(selectSelectedTasksList);
    const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date
    const thirtyMinLayout = useSelector(selectThirtyMinLayout);

    //#endregion

    //#region local states
    const [timelineHour, setTimelineHour] = useState(0);                            // holds current hh:mm
    const [timelineIndex, setTimelineIndex] = useState(0);                          // holds current index of 30 minute period
    const [timelinePercentage, setTimelinePercentage] = useState(0);                // holds position of line relative to minute in hour
    const [shiftStart, setShiftStart] = useState("")                                // holds value of employee shift start time
    const [shiftEnd, setShiftEnd] = useState("")                                    // holds value of employee shift end time
    //#endregion

    //#region useEffect

    // draw time line
    useEffect(() => {
        const updateTimeline = () => {
            const currentDate = new Date();

            // Get current hour and minute
            const currentHour = currentDate.getHours();
            const currentMinute = currentDate.getMinutes();

            // Format the time to the nearest 30-minute block
            const formattedTime = formatTimeToNearest30();
            setTimelineHour(formattedTime);

            // Determine the current 5-minute interval index
            const intervalIndex = getFiveMinuteIntervalIndex(currentDate);
            setTimelineIndex(intervalIndex);

            // Calculate the minutes passed within the 5-minute interval
            const minutesSincePreviousHalfHour = currentMinute % 30; // Minutes into the current 30-minute block
            const minutesInCurrentInterval = minutesSincePreviousHalfHour % 5; // Minutes into the current 5-minute interval

            // Calculate the percentage within the 5-minute interval
            const percentage = minutesInCurrentInterval * minute30MinLayoutWidth // add offset
            setTimelinePercentage(percentage);
            // setTimelinePercentage((currentMinute * 100) / 60);

        };

        // Initial update
        updateTimeline();

        // Schedule updates every 60 seconds
        const intervalId = setInterval(updateTimeline, 60000);

        // Clean up the interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    // listener to set start and end shift time 
    useEffect(() => {
        if (props?.employee?.shift) {
            const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }
            let tempStartsShiftDate = convertTimeStampToDate(props?.employee?.shift?.start, options)
            let startShiftYear = tempStartsShiftDate?.getFullYear();
            let startShiftDay = tempStartsShiftDate?.getDay();
            let currentYear = new Date(timelineDate).getFullYear();
            let currentDay = new Date(timelineDate).getDay();
            // check if employee shift is in current day 
            if (startShiftYear == currentYear && startShiftDay == currentDay) {
                setShiftStart(convertShiftTimeStamp(props?.employee?.shift?.start))
                setShiftEnd(convertShiftTimeStamp(props?.employee?.shift?.end))
            }
        }
    }, [props?.employee?.shift, timelineDate])

    //#endregion

    //#region functions

    // takes a time string and an interval and returns the correct time string
    const getTimeForInterval = (baseTime, interval) => {
        // Parse the base time string into hours and minutes
        const [hours, minutes] = baseTime.split(':').map(Number);

        // Add the interval (5 minutes per interval)
        const totalMinutes = hours * 60 + minutes + interval * 5;

        // Calculate the new hours and minutes
        const newHours = Math.floor(totalMinutes / 60) % 24; // Keep within 24 hours
        const newMinutes = totalMinutes % 60;

        // Format the new time as HH:mm
        return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    };

    // get the time line row clicked column
    const handleColumnClick = (column, interval) => {
        // check if there is a selcted task list to assign
        if (Array.isFullArray(selectedTasksList)) {

            /* create id to save which column was clicked */
            // use column and interval to calculate which interval column the task should be placed at */
            let time = getTimeForInterval(column, interval);

            const columnId = time + "-" + props?.employee?.employeeId;

            props?.setClickedColumn(columnId);

            // show confirmation modal
            dispatch(setShowConfirmationModal(true));
            dispatch(setClickedEmployeeId(props.employee.userId))
        }
    };

    const openBreakTaskModal = () => {
        dispatch(setShowBreakTaskModal(true))
        dispatch(setBreakTaskEmployee(props?.employee))
    }

    function isSameDay(d1, d2) {
        return (
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate()
        );
    }

    //#endregion

    const intervals = Array.from({ length: 6 }); // 6 intervals for 5-minute blocks in 30 minutes

    return (
        <>
            <div style={{ display: "flex" }}>

                {/* employee profile pic, name etc. */}
                <div style={{ position: "sticky", left: 0, zIndex: 200 }}>
                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", height: "100%", width: EmployeeInformationColumnWidth, borderRight: "1px solid lightgrey", backgroundColor: "white" }}>
                        <EmployeeInformation id={props?.employee?.employeeId} employeeShift={props?.employee?.shift} openBreakTaskModal={openBreakTaskModal} employee={props?.employee} index={props?.index} employeeArrayLength={props?.arrayLength} />
                    </div>
                </div>

                {/* timeline columns */}
                {timelineData && timelineData?.columnIds && timelineData?.columnIds.map && timelineData?.columnIds?.map((columnId, index) => {
                    return (
                        <div key={index} style={{  /* borderStyle:'solid', borderWidth:1 */ }}>

                            <TimelineThirtyMinColumnContainer
                                backgroundColor={columnId >= shiftStart && columnId <= shiftEnd ? "#F5F5F5" : "white"}
                                borderColor={props?.clickedColumn == columnId + "-" + props?.employee?.employeeId ? Colors.PRM_LIGHT_BLUE : "lightgrey"}
                                onClick={(interval) => { handleColumnClick(columnId, interval) }}
                                is30MinLayout={thirtyMinLayout}

                            >

                                {/*  5 min interval lines */}
                                {intervals.map((_, index) => (
                                    <IntervalLine key={index} style={{ left: `${(index * 100) / 6}%`, }}>

                                        {/* Red line div */}
                                        {columnId == timelineHour && timelineIndex == index && isSameDay(new Date(), timelineDate) &&
                                            <div style={{ position: "absolute", top: 0, bottom: 0, left: timelinePercentage, width: "2px", backgroundColor: "red" }} />
                                        }

                                        {/* list of employee jobs within column */}
                                        <ThirtyMinLayoutJobList
                                            employeeId={props?.employee?.employeeId}
                                            hour={columnId}
                                            id={columnId + "-" + props?.employee?.employeeId}
                                            interval={index}
                                            disabled={true}
                                        />

                                    </IntervalLine>
                                ))}


                            </TimelineThirtyMinColumnContainer>
                        </div>


                    );
                })}
            </div>
        </>
    );
};

export default TimelineThirtyMinRow;